import { takeUntil, takeWhile } from 'rxjs/operators';
import { FolderableType } from 'FoldersApp/enums/folderable-type.enum';
import { ToastMessageType } from 'NewAngular/features/components/toast-messages/toast-messages.enum';
import { FolderableMoveStrategy } from './folderable-move-strategy';
export class FolderableBulkMoveStrategy extends FolderableMoveStrategy {
    get isValidDropTarget() {
        const { isDragging, folderable, ancestorInfo, resourceType } = this.directive;
        // Cannot drop item onto itself
        if (isDragging)
            return false;
        if (resourceType === 'folder') {
            // Can drop folder item onto a folder that the user has edit permission for
            return folderable.item.permissions.canEdit;
        }
        // If dropping the item onto a file,
        // assume the user is trying to move the item into the folder that contains this file.
        // This allows moving items into a folder without scrolling to find the folder's name.
        if (ancestorInfo.id === null) {
            // Users can always move items in MyYCharts
            return true;
        }
        // We expect a folder to have a permissions block
        return ancestorInfo.permissions.canEdit;
    }
    constructor(folderableDirective) {
        super(folderableDirective);
        this.SELECTION_GHOST_IMAGE_ID = 'bulk-action-selection-ghost-image';
        const { bulkActionSelectionService, setIsDraggable, unsubscribe, resourceType, itemId } = this.directive;
        bulkActionSelectionService.idsByResourceType$
            .pipe(takeWhile(() => bulkActionSelectionService.bulkActionMode), takeUntil(unsubscribe))
            .subscribe(() => {
            const draggable = bulkActionSelectionService.bulkActionMode &&
                bulkActionSelectionService.isSelected(resourceType, itemId);
            setIsDraggable.bind(this.directive)(draggable);
        });
    }
    dropEventHandler(event) {
        const { id, itemType } = this.getDragEventData(event);
        if (id === null || itemType === null)
            return;
        // If the user is dropping the item on a folder, attempt to move the item into the folder.
        // If the user is dropping the item on a file,
        // assume they are trying to move an item into the file's parent folder.
        const { ancestorInfo, resourceType, itemId } = this.directive;
        const targetFolderId = resourceType === FolderableType.FOLDER ? itemId : ancestorInfo.id;
        const { bulkActionSelectionService, folderOperationsService, toastMessagesService, folderableCollectionService, } = this.directive;
        folderOperationsService
            .bulkMoveFolderableItems(bulkActionSelectionService.currentItems, targetFolderId)
            .subscribe({
            next: (response) => {
                toastMessagesService.addMessage(response.message);
                folderableCollectionService.refreshData();
            },
            error: (response) => {
                toastMessagesService.addMessage(response.error.error_message, ToastMessageType.ERROR);
            },
        });
    }
    drop(event, parentMethod) {
        parentMethod(event);
    }
    dragenter(event, parentMethod) {
        parentMethod(event);
    }
    dragover(event, parentMethod) {
        parentMethod(event);
    }
    dragend(event, parentMethod) {
        parentMethod(event);
        this.deleteDragImage();
    }
    dragleave(event, parentMethod) {
        parentMethod(event);
    }
    setEventData(event) {
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.effectAllowed = 'move';
        // Drag data must be set in the dragstart event; cannot be set in other events
        const { itemId, resourceType } = this.directive;
        event.dataTransfer.setData('id', `${itemId}`);
        event.dataTransfer.setData('itemType', `${resourceType}`);
        const elem = this.createDragImage();
        event.dataTransfer.setDragImage(elem, 0, 0);
    }
    getDragEventData(event) {
        let id = parseInt(event.dataTransfer.getData('id'), 10);
        const itemType = event.dataTransfer.getData('itemType') || null;
        const { effectAllowed } = event.dataTransfer;
        if (Number.isNaN(id))
            id = null;
        return { id, itemType, effectAllowed };
    }
    createDragImage() {
        const { bulkActionSelectionService, elemRef } = this.directive;
        const folderableElement = elemRef.nativeElement;
        const labelElem = folderableElement.getElementsByClassName('td file-td-name')[0];
        // Create an html element to generate a ghost image for bulk drag-drop.
        // The image will be based on the element that was clicked for dragging
        // with a badge of the total count of selected items.
        const labelContainer = labelElem.cloneNode(true);
        labelContainer.id = this.SELECTION_GHOST_IMAGE_ID;
        labelContainer.classList.add('bg-yc-blue-95', 'width-400', 'mt-10', 'mb-10', 'mr-12', 'ml-12');
        const countElem = document.createElement('div');
        countElem.textContent = bulkActionSelectionService.numSelectedItems.toString();
        countElem.classList.add('badge', 'bg-yc-blue-75');
        const label = labelContainer.getElementsByClassName('file-td-wrap')[0];
        label.appendChild(countElem);
        document.body.appendChild(labelContainer);
        return labelContainer;
    }
}
